import { gql } from "@apollo/client";
import { FetchResult, MutationOptions } from "@apollo/client";
import { jssaMutation } from "./index";
import { queryShopifyProductsByTitle } from "./ShopifyProductDataStore";
import { ProductOption } from "../common/ProductAutocompleteMultiSelector";

// ProductOption (the autocomplete/chip shape) is defined by the reusable
// ProductAutocompleteMultiSelector and re-exported here for this feature's
// consumers (manager, converter).
export type { ProductOption };

/** Counts returned by the `resyncShippingTimelines` mutation. */
export interface ResyncResult {
    recomputedLineItemCount: number;
    recomputedOrderCount: number;
    backfilledCount: number;
    elapsedMs: number;
}

export const resyncShippingTimelinesMutation = gql`
    mutation resyncShippingTimelines($productIds: [String!]) {
        resyncShippingTimelines(productIds: $productIds) {
            recomputedLineItemCount
            recomputedOrderCount
            backfilledCount
            elapsedMs
        }
    }
`;

type SearchProductsFn = (titleContains: string, limit?: number) => Promise<ProductOption[]>;
type MutateFn = <T = { resyncShippingTimelines: ResyncResult }>(
    options: MutationOptions<T>,
) => Promise<FetchResult<T>>;

/**
 * Data layer for the Resync Shipping Timelines admin page (Class + DI +
 * Singleton). Business logic — searching products, building the productIds
 * payload, running the mutation and mapping its result — lives here rather than
 * in the React component.
 */
export class ShippingTimelineDataStore {
    private readonly _searchProducts: SearchProductsFn;
    private readonly _mutate: MutateFn;

    public constructor(
        searchProducts: SearchProductsFn = queryShopifyProductsByTitle,
        mutate: MutateFn = jssaMutation,
    ) {
        this._searchProducts = searchProducts;
        this._mutate = mutate;
    }

    /** Typeahead search for products by (case-insensitive) title substring. */
    public async searchProductsByTitle(titleContains: string, limit = 20): Promise<ProductOption[]> {
        const trimmed = titleContains.trim();
        if (trimmed.length === 0) {
            return [];
        }
        return this._searchProducts(trimmed, limit);
    }

    /**
     * Trigger a synchronous shipping-timeline resync.
     *
     * @param productIds Shopify product ids to scope the recompute to. An empty
     *   array is sent as `undefined`, which the server treats as "recompute ALL
     *   unfulfilled order line items".
     */
    public async resync(productIds: string[]): Promise<ResyncResult> {
        const scoped = productIds.length > 0 ? productIds : undefined;
        const { data } = await this._mutate<{ resyncShippingTimelines: ResyncResult }>({
            mutation: resyncShippingTimelinesMutation,
            variables: { productIds: scoped },
        });
        if (!data?.resyncShippingTimelines) {
            throw new Error("Resync did not return a result");
        }
        return data.resyncShippingTimelines;
    }
}

export const shippingTimelineDataStore = new ShippingTimelineDataStore();
